import { useMemo, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import type { DraftResponse, DraftState, PlayerSummary } from '../../types/protocol'

interface Props {
  draft: DraftState
  players: PlayerSummary[]
  commissionerToken: string
  onClose: () => void
}

const RESULT_LIMIT = 40

// One modal for both jobs the commissioner has here: picking for whoever is on
// the clock, and going back to fill a pick that was skipped earlier.
export default function ForcePickModal({ draft, players, commissionerToken, onClose }: Props) {
  const skipped = useMemo(
    () => draft.picks.filter((pick) => pick.status === 'skipped'),
    [draft.picks],
  )
  const currentPick = draft.picks.find((pick) => pick.overall === draft.current_pick_overall)

  const [target, setTarget] = useState<number | 'current'>('current')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const takenIds = useMemo(
    () => new Set(draft.picks.filter((pick) => pick.player_id !== null).map((pick) => pick.player_id)),
    [draft.picks],
  )

  const matches = useMemo(() => {
    const available = players.filter((player) => !takenIds.has(player.id))
    const term = search.trim().toLowerCase()
    if (!term) return available.slice(0, RESULT_LIMIT)
    return available
      .filter(
        (player) =>
          player.full_name.toLowerCase().includes(term) ||
          player.position.toLowerCase().includes(term) ||
          (player.nfl_team ?? '').toLowerCase().includes(term),
      )
      .slice(0, RESULT_LIMIT)
  }, [players, takenIds, search])

  const targetPick = target === 'current' ? currentPick : draft.picks.find((p) => p.overall === target)
  const targetTeam = targetPick ? draft.teams.find((team) => team.id === targetPick.team_id) : null

  async function submit() {
    if (selected === null) return
    setBusy(true)
    setError(null)
    try {
      await api<DraftResponse>(`/drafts/${draft.join_code}/picks`, {
        method: 'POST',
        body: {
          player_id: selected,
          ...(target === 'current' ? {} : { pick_overall: target }),
        },
        commissionerToken,
      })
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not make that pick')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/80 p-4">
      <div className="w-full max-w-lg rounded-xl border border-line bg-surface-1 p-5">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="eyebrow">Force a pick</h3>
          <button type="button" onClick={onClose} className="text-sm text-ink-mid hover:text-ink-hi">
            Close
          </button>
        </div>

        <label className="mt-4 flex flex-col gap-1 text-sm text-ink-mid">
          Which pick
          <select
            value={target}
            onChange={(event) =>
              setTarget(event.target.value === 'current' ? 'current' : Number(event.target.value))
            }
            className="rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
          >
            <option value="current" disabled={!currentPick}>
              {currentPick
                ? `On the clock — R${currentPick.round}.${currentPick.slot}`
                : 'Nothing is on the clock'}
            </option>
            {skipped.map((pick) => {
              const team = draft.teams.find((t) => t.id === pick.team_id)
              return (
                <option key={pick.overall} value={pick.overall}>
                  Backfill skipped R{pick.round}.{pick.slot} — {team?.name ?? 'Unknown team'}
                </option>
              )
            })}
          </select>
        </label>

        <p className="mt-2 text-sm text-ink-mid">
          Picking for <span className="font-medium text-ink-hi">{targetTeam?.name ?? '—'}</span>
        </p>

        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search players…"
          className="mt-4 w-full rounded border border-line-strong bg-ink px-3 py-2 text-ink-hi"
        />

        <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto">
          {matches.map((player) => (
            <li key={player.id}>
              <button
                type="button"
                onClick={() => setSelected(player.id)}
                className={`flex w-full items-baseline gap-2 rounded px-2 py-1.5 text-left text-sm ${
                  selected === player.id ? 'bg-signal/20 text-signal' : 'hover:bg-surface-3'
                }`}
              >
                <span className="w-10 shrink-0 text-xs tabular-nums text-ink-lo">
                  {draft.rankings_count > 0 && !player.custom_rank
                    ? `(${player.effective_rank})`
                    : player.effective_rank}
                </span>
                <span className="min-w-0 flex-1 truncate">{player.full_name}</span>
                <span className="shrink-0 text-xs text-ink-lo">
                  {player.position === 'DEF' ? 'DST' : player.position}
                  {player.nfl_team ? ` · ${player.nfl_team}` : ''}
                </span>
              </button>
            </li>
          ))}
          {matches.length === 0 && <li className="px-2 py-1.5 text-sm text-ink-lo">No players match.</li>}
        </ul>

        {error && <p className="mt-3 text-sm text-crit">{error}</p>}

        <button
          type="button"
          disabled={selected === null || busy || !targetPick}
          onClick={() => void submit()}
          className="mt-4 w-full rounded bg-signal px-4 py-2 font-semibold text-ink hover:bg-signal-dim disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-ink-lo"
        >
          {busy ? 'Submitting…' : 'Make this pick'}
        </button>
      </div>
    </div>
  )
}
